import { useId } from 'react'

import {
  IMG_H,
  IMG_W,
  cropFor,
  hashSeed,
  mulberry32,
  type Crop,
} from '@/components/pattern-field'
import { cn } from '@/lib/utils'

/**
 * The compact mark on the home page's "Explore Our Latest Insights" cards.
 *
 * A 2x2 checkerboard pinned to the card's top-left corner: the top-left and
 * bottom-right cells are windows into one of the site's gradient artworks
 * (the same crop technique as the tile mosaic in pattern-field), the other
 * two cells are left open so the card surface shows through.
 *
 * The two filled cells show ONE continuous piece of the artwork. A single crop
 * covers the whole 2x2 square and masks then reveal only the filled cells, so
 * what appears in the bottom-right cell is exactly the part of the picture
 * that would sit there if the open cells were filled too. Cropping each cell
 * separately reads as two unrelated swatches, which is not the reference.
 *
 * Hover: the bottom-right cell enlarges, growing outward from its own
 * top-left corner (the point where the two filled cells touch), while the
 * top-left cell and the artwork itself stay put. Because both cells look
 * through the same window, the growing cell simply uncovers more of the same
 * picture rather than zooming it. The two cells are separate masked layers so
 * only the second one carries the transform. `group-hover` is driven by the
 * card <Link>, which already has the `group` class.
 *
 * Everything is drawn from the seed the caller passes (the post's href): which
 * gradient the square crops from and where the crop lands. That keeps the mark
 * stable across renders and between the server and client passes, so
 * hydration stays quiet without any client-side re-roll.
 */

/** The same six artworks the product and blog thumbs crop from. */
export const INSIGHT_ART = [
  '/gradients/01.webp',
  '/gradients/02.webp',
  '/gradients/03.webp',
  '/gradients/04.webp',
  '/gradients/05.webp',
  '/gradients/06.webp',
] as const

/** Side of one cell in viewBox units; the whole mark is 2x2 of these. */
const CELL = 160
const SIZE = CELL * 2
/** How much the bottom-right cell grows on hover. */
const HOVER_SCALE = 1.18
/**
 * The artwork window is drawn larger than the 2x2 square by the hover
 * overhang, so when the bottom-right cell enlarges past the square there is
 * still picture behind it to reveal (a window exactly the square's size would
 * leave the grown margin empty). The extra extends right and down only, off
 * the two open cells' side, so the resting look is unchanged.
 */
const OVERHANG = Math.ceil(CELL * (HOVER_SCALE - 1))
const WINDOW = SIZE + OVERHANG

/**
 * Deal a distinct artwork to each seed. Each seed's own draw is the first
 * choice; if a card earlier in the list already holds it, walk forward to the
 * next free one. Deterministic on list order, so a row of cards never shows
 * the same gradient twice while still keying off each card's seed.
 */
export function dealInsightArt(seeds: readonly string[]): string[] {
  const taken = new Set<string>()
  return seeds.map((seed) => {
    const rand = mulberry32(hashSeed(seed))
    const start = Math.floor(rand() * INSIGHT_ART.length)
    // With more seeds than artworks the pool runs dry; the raw draw stands.
    let art: string = INSIGHT_ART[start]!
    for (let step = 0; step < INSIGHT_ART.length; step += 1) {
      const candidate = INSIGHT_ART[(start + step) % INSIGHT_ART.length]!
      if (!taken.has(candidate)) {
        art = candidate
        break
      }
    }
    taken.add(art)
    return art
  })
}

export function InsightThumb({
  seed,
  art,
  className,
}: {
  /** Anything stable per card; the href is what callers pass. */
  seed: string
  /**
   * Artwork override, for callers that deal gradients across a set of cards
   * (see `dealInsightArt`). Left out, the seed picks one.
   */
  art?: string
  className?: string
}) {
  const rand = mulberry32(hashSeed(seed))
  const drawn = INSIGHT_ART[Math.floor(rand() * INSIGHT_ART.length)]!
  const artwork = art ?? drawn
  // One crop for the whole window (the 2x2 square plus hover overhang).
  const crop: Crop = cropFor(rand, { x: 0, y: 0, s: WINDOW, w: WINDOW })
  // useId emits ":r0:", and colons break url(#...) references.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const maskTL = `checker-tl-${uid}`
  const maskBR = `checker-br-${uid}`

  // The full-square window, drawn once per masked layer. Both layers must
  // hold an identical copy: a mask clips its own subtree only, so sharing one
  // <svg> between two <g mask> parents is not possible in SVG.
  const window = (
    <svg
      x={0}
      y={0}
      width={WINDOW}
      height={WINDOW}
      viewBox={`${crop.vx} ${crop.vy} ${crop.vw} ${crop.vh}`}
      preserveAspectRatio="xMidYMid slice"
    >
      <image
        href={artwork}
        width={IMG_W}
        height={IMG_H}
        preserveAspectRatio="none"
      />
    </svg>
  )

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      // overflow-visible: the enlarged bottom-right cell grows past the
      // square's own bounds on hover, and the default overflow:hidden on
      // inline <svg> would clip it.
      className={cn('block h-full w-full overflow-visible', className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <mask id={maskTL} maskUnits="userSpaceOnUse">
          <rect x={0} y={0} width={CELL} height={CELL} fill="#fff" />
        </mask>
        <mask id={maskBR} maskUnits="userSpaceOnUse">
          {/* The reveal rect is what grows on hover, anchored at the corner
              the two filled cells share (CELL, CELL). Scaling the rect and
              not the artwork means the cell uncovers MORE of the same
              picture instead of zooming what it already shows. transform-box
              fill-box makes origin-top-left resolve to the rect's own corner. */}
          <rect
            x={CELL}
            y={CELL}
            width={CELL}
            height={CELL}
            fill="#fff"
            // The 1.18 here must match HOVER_SCALE above (Tailwind needs the
            // literal at build time; OVERHANG is derived from the constant).
            className={cn(
              '[transform-box:fill-box] origin-top-left',
              'transition-transform duration-500 ease-out group-hover:scale-[1.18]',
              'motion-reduce:transition-none motion-reduce:group-hover:scale-100'
            )}
          />
        </mask>
      </defs>

      {/* Top-left cell: static. */}
      <g mask={`url(#${maskTL})`} className="opacity-90 dark:opacity-80">
        {window}
      </g>

      {/* Bottom-right cell: same fixed window, revealed through the mask
          whose rect enlarges on hover. */}
      <g mask={`url(#${maskBR})`} className="opacity-90 dark:opacity-80">
        {window}
      </g>
    </svg>
  )
}

export default InsightThumb
